import PageComponent from "../components/page/PageComponent.vue";
import SectionSheet from "../components/section_sheet/SectionSheet.vue";
import PageControl from "../components/page_control/PageControl.vue";
import { format_date } from "@/utils/date_formatter";

export default {
  name: "CipcReport",
  components: { PageControl, PageComponent, SectionSheet },
  props: {
    sheet_elements: {
      type: String,
      default: ".sheet",
      required: false,
    },
    cipc_data: {
      type: Object,
      default: () => ({}),
      required: false,
    },
  },
  data() {
    return {
      export_options: {
        company_information: false,
        director_information: false,
        address_information: false,
        raw_json: false,
      },
      export_format: "csv",
      libraries_loaded: {
        papa_parse: false,
        sheetjs: false,
      },
    };
  },
  computed: {
    has_selected_tables() {
      return Object.values(this.export_options).some((option) => option);
    },
    safe_cipc_data() {
      return this.cipc_data?.Commercial || {};
    },

    // Basic Company Information
    company_name() {
      return (
        this.safe_cipc_data.CommercialBusinessInformation?.CommercialName ||
        this.safe_cipc_data.CommercialCompanyInformation?.CommercialName ||
        "N/A"
      );
    },

    registration_number() {
      return (
        this.safe_cipc_data.CommercialBusinessInformation?.RegistrationNo ||
        this.safe_cipc_data.CommercialCompanyInformation?.RegistrationNo ||
        "N/A"
      );
    },

    tax_number() {
      return (
        this.safe_cipc_data.CommercialBusinessInformation?.TaxNo ||
        this.safe_cipc_data.CommercialCompanyInformation?.TaxNo ||
        "N/A"
      );
    },

    vat_number() {
      return (
        this.safe_cipc_data.CommercialBusinessInformation?.VATNo ||
        this.safe_cipc_data.CommercialCompanyInformation?.VATNo ||
        "Not Available"
      );
    },

    commercial_status() {
      return (
        this.safe_cipc_data.CommercialBusinessInformation?.CommercialStatus ||
        this.safe_cipc_data.CommercialCompanyInformation?.CommercialStatus ||
        "N/A"
      );
    },

    commercial_type() {
      return (
        this.safe_cipc_data.CommercialBusinessInformation?.CommercialType ||
        this.safe_cipc_data.CommercialCompanyInformation?.CommercialType ||
        "N/A"
      );
    },

    age_of_business() {
      return (
        this.safe_cipc_data.CommercialBusinessInformation?.AgeofBusiness ||
        "N/A"
      );
    },

    sic_code() {
      return (
        this.safe_cipc_data.CommercialBusinessInformation?.SIC ||
        this.safe_cipc_data.CommercialCompanyInformation?.SIC ||
        "N/A"
      );
    },

    financial_year_end() {
      return (
        this.safe_cipc_data.CommercialBusinessInformation?.FinancialYearEnd ||
        this.safe_cipc_data.CommercialCompanyInformation?.FinancialYearEnd ||
        "N/A"
      );
    },

    registration_date() {
      return (
        this.safe_cipc_data.CommercialBusinessInformation?.RegistrationDate ||
        this.safe_cipc_data.CommercialCompanyInformation?.RegistrationDate ||
        null
      );
    },

    business_start_date() {
      return (
        this.safe_cipc_data.CommercialBusinessInformation?.BusinessStartDate ||
        this.safe_cipc_data.CommercialCompanyInformation?.BusinessStartDate ||
        null
      );
    },

    formatted_registration_date() {
      return this.registration_date
        ? this.formatDate(this.registration_date)
        : "N/A";
    },

    formatted_business_start_date() {
      return this.business_start_date
        ? this.formatDate(this.business_start_date)
        : "N/A";
    },

    authorised_shares() {
      return (
        this.safe_cipc_data.CommercialBusinessInformation
          ?.AuthorisedNoOfShares || "N/A"
      );
    },

    issued_shares() {
      return (
        this.safe_cipc_data.CommercialBusinessInformation?.IssuedNoOfShares ||
        "N/A"
      );
    },

    director_count() {
      return (
        this.safe_cipc_data.CommercialBusinessInformation?.DirectorCount ||
        this.safe_cipc_data.CommercialCompanyInformation?.DirectorCount ||
        "0"
      );
    },

    // NEW: Multiple Directors Information - Active Directors
    active_directors() {
      const activeDirectors = this.normalize_to_array(this.safe_cipc_data.CommercialActivePrincipalInformation);
      const principalDirectors = this.normalize_to_array(this.safe_cipc_data.CommercialPrincipalInformation);

      // Combine and deduplicate by ID number
      const allActive = [...activeDirectors, ...principalDirectors.filter(p => p.DirectorStatusCode === 'Active')];
      const uniqueActive = allActive.filter((director, index, self) =>
        index === self.findIndex(d => d.IDNo === director.IDNo)
      );

      return uniqueActive;
    },

    // NEW: All Directors (Active + Resigned)
    all_directors() {
      const allDirectors = this.normalize_to_array(this.safe_cipc_data.CommercialDirectorInformation);
      const principalDirectors = this.normalize_to_array(this.safe_cipc_data.CommercialPrincipalInformation);
      const previousDirectors = this.normalize_to_array(this.safe_cipc_data.CommercialPreviousPrincipalInformation);

      // Combine all sources and deduplicate by ID number
      const combined = [...allDirectors, ...principalDirectors, ...previousDirectors];
      const unique = combined.filter((director, index, self) =>
        index === self.findIndex(d => d.IDNo === director.IDNo)
      );

      return unique.sort((a, b) => {
        // Sort by status (Active first) then by name
        if (a.DirectorStatusCode !== b.DirectorStatusCode) {
          return a.DirectorStatusCode === 'Active' ? -1 : 1;
        }
        return (a.FullName || a.Fullname || '').localeCompare(b.FullName || b.Fullname || '');
      });
    },

    // Address Information
    address_information() {
      return this.safe_cipc_data.CommercialAddressInformation || [];
    },

    // Transaction Details
    client_user_reference() {
      return (
        this.safe_cipc_data.SubscriberInputDetails?.Reference ||
        this.safe_cipc_data.CommercialBusinessInformation?.ReferenceNo ||
        "N/A"
      );
    },

    current_date() {
      return new Date().toISOString();
    },

    formatted_current_date() {
      return this.formatDate(this.current_date);
    },

    // Report metadata
    report_reference() {
      return this.client_user_reference;
    },

    // Badge classes and icons for company status
    company_status_badge_class() {
      return this.commercial_status === "In Business"
        ? "status-good"
        : "status-bad";
    },

    company_status_icon() {
      return this.commercial_status === "In Business"
        ? "fas fa-check-circle"
        : "fas fa-times-circle";
    },

    modal_instance() {
      var modal = new window.bootstrap.Modal(
        document.getElementById(`export-modal-${this.report_reference}`)
      );
      return modal;
    },

    directors_per_page() {
      return 10; // Adjust as needed for your layout
    },

    paginatedDirectors() {
      const directors = this.all_directors;
      const pageSize = this.directors_per_page;
      const pages = [];
      for (let i = 0; i < directors.length; i += pageSize) {
        pages.push(directors.slice(i, i + pageSize));
      }
      return pages;
    },

    // If you have a separate contact info table, paginate similarly:
    paginatedDirectorContacts() {
      const directors = this.all_directors;
      const pageSize = this.directors_per_page;
      const pages = [];
      for (let i = 0; i < directors.length; i += pageSize) {
        pages.push(directors.slice(i, i + pageSize));
      }
      return pages;
    },

  },

  methods: {
    close_modal() {
      this.modal_instance.hide();
    },

    // Utility methods
    formatDate(date) {
      return format_date(date);
    },

    format_address(address) {
      const parts = [
        address.Address1,
        address.Address2,
        address.Address3,
        address.Address4,
      ].filter((part) => part && typeof part === 'string' && part.trim() !== "");

      return parts.join(", ") || "Not Available";
    },

    // NEW: Get director status badge class
    get_director_status_badge_class(director) {
      return director.DirectorStatusCode === "Active" ? "status-good" : "status-bad";
    },

    // NEW: Get director status icon
    get_director_status_icon(director) {
      return director.DirectorStatusCode === "Active"
        ? "fas fa-check-circle"
        : "fas fa-times-circle";
    },

    // NEW: Get director full name from various data structures
    get_director_full_name(director) {
      return director.FullName || director.Fullname || 
             `${director.FirstName || ''} ${director.Surname || ''}`.trim() || "N/A";
    },

    // NEW: Get director contact information
    get_director_contact_info(director) {
      // Always fetch contact info from CommercialPrincipalInformation by IDNo
      const principalList = this.normalize_to_array(this.safe_cipc_data.CommercialPrincipalInformation);
      const principal = principalList.find(p => p.IDNo === director.IDNo);

      return {
        cellular: principal?.CellularNo || "Not Available",
        email: principal?.EmailAddress || "Not Available", 
        home_phone: principal?.HomeTelephoneNo || "Not Available",
        work_phone: principal?.WorkTelephoneNo || "Not Available"
      };
    },

    // Export functionality
    show_export_modal() {
      this.export_options.company_information = true;
      this.export_options.director_information = true;
      this.export_options.address_information = true;
      this.export_options.raw_json = false;
      this.modal_instance.show();
    },

    async load_export_libraries() {
      if (!this.libraries_loaded.papa_parse) {
        await this.load_script(
          "https://cdnjs.cloudflare.com/ajax/libs/PapaParse/5.3.0/papaparse.min.js"
        );
        this.libraries_loaded.papa_parse = true;
      }

      if (!this.libraries_loaded.sheetjs) {
        await this.load_script(
          "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.17.0/xlsx.full.min.js"
        );
        this.libraries_loaded.sheetjs = true;
      }
    },

    load_script(src) {
      return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = src;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },

    async export_data() {
      await this.load_export_libraries();

      if (this.export_format === "csv") {
        await this.export_csv();
      } else if (this.export_format === "excel") {
        await this.export_excel();
      } else if (this.export_format === "json") {
        await this.export_json();
      }

      // Close modal
      this.close_modal();
    },

    // Prepare company information data
    prepare_company_information_data() {
      return [
        {
          "Report Reference": this.report_reference,
          "Company Name": this.company_name,
          "Registration Number": this.registration_number,
          "Tax Number": this.tax_number,
          "VAT Number": this.vat_number,
          "Company Status": this.commercial_status,
          "Company Type": this.commercial_type,
          "Registration Date": this.formatted_registration_date,
          "Business Start Date": this.formatted_business_start_date,
          "Financial Year End": this.financial_year_end,
          "SIC Code": this.sic_code,
          "Business Age": this.age_of_business,
          "Director Count": this.director_count,
          "Authorised Shares": this.authorised_shares,
          "Issued Shares": this.issued_shares,
          "Generated Date": this.formatted_current_date,
        },
      ];
    },

    // UPDATED: Prepare director information data for ALL directors
    prepare_director_information_data() {
      return this.all_directors.map(director => {
        const contact = this.get_director_contact_info(director);
        return {
          "Full Name": this.get_director_full_name(director),
          "ID Number": director.IDNo || "N/A",
          "Birth Date": director.BirthDate ? this.formatDate(director.BirthDate) : "N/A",
          "Age": director.Age || "N/A",
          "Director Status": director.DirectorStatusCode || "N/A",
          "Appointment Date": director.AppointmentDate ? this.formatDate(director.AppointmentDate) : "N/A",
          "Resignation Date": director.ResignationDate ? this.formatDate(director.ResignationDate) : "N/A",
          "Years with Business": director.YearsWithBusiness || director.YearsWithBus || "N/A",
          "SA Resident": director.ISRSAResident || "N/A",
          "Cellular Number": contact.cellular,
          "Email Address": contact.email,
          "Home Telephone": contact.home_phone,
          "Work Telephone": contact.work_phone,
          "Physical Address": director.PhysicalAddress || "N/A",
          "Postal Address": director.PostalAddress || "N/A"
        };
      });
    },

    // Prepare address information data
    prepare_address_information_data() {
      return this.address_information.map((address) => ({
        "Address Type": address.AddressType,
        "Address Line 1": address.Address1 || "",
        "Address Line 2": address.Address2 || "",
        "Address Line 3": address.Address3 || "",
        "Address Line 4": address.Address4 || "",
        "Full Address": this.format_address(address),
        "Postal Code": address.PostalCode || "",
        "Last Updated": this.formatDate(address.LastUpdatedDate),
      }));
    },

    // Prepare raw JSON data
    prepare_raw_json_data() {
      return {
        report_metadata: {
          report_reference: this.report_reference,
          client_user_reference: this.client_user_reference,
          generated_date: this.formatted_current_date,
          export_date: new Date().toISOString(),
          report_type: "CIPC Company Verification Report",
        },
        raw_cipc_data: this.cipc_data,
      };
    },

    // Export methods
    async export_csv() {
      const Papa = window.Papa;
      const current_date = new Date().toISOString().split("T")[0];

      // Map section keys to their data preparation methods and file suffixes
      const sectionMap = [
        {
          key: "company_information",
          label: "Company_Information",
          prepare: this.prepare_company_information_data,
        },
        {
          key: "director_information",
          label: "Director_Information",
          prepare: this.prepare_director_information_data,
        },
        {
          key: "address_information",
          label: "Address_Information",
          prepare: this.prepare_address_information_data,
        },
      ];

      // For each selected section, generate and download a CSV
      for (const section of sectionMap) {
        if (this.export_options[section.key]) {
          const data = section.prepare.call(this);
          if (data && data.length > 0) {
            const csv = Papa.unparse(data);
            const filename = `CIPC_Company_Report_${section.label}_${current_date}.csv`;
            this.download_file(csv, filename, "text/csv");
          }
        }
      }
    },

    async export_excel() {
      const XLSX = window.XLSX;
      const current_date = new Date().toISOString().split("T")[0];
      const workbook = XLSX.utils.book_new();

      if (this.export_options.company_information) {
        const company_data = this.prepare_company_information_data();
        const worksheet = XLSX.utils.json_to_sheet(company_data);
        XLSX.utils.book_append_sheet(
          workbook,
          worksheet,
          "Company Information"
        );
      }

      if (this.export_options.director_information) {
        const director_data = this.prepare_director_information_data();
        const worksheet = XLSX.utils.json_to_sheet(director_data);
        XLSX.utils.book_append_sheet(
          workbook,
          worksheet,
          "Director Information"
        );
      }

      if (this.export_options.address_information) {
        const address_data = this.prepare_address_information_data();
        const worksheet = XLSX.utils.json_to_sheet(address_data);
        XLSX.utils.book_append_sheet(
          workbook,
          worksheet,
          "Address Information"
        );
      }

      if (this.export_options.raw_json) {
        const json_data = this.prepare_raw_json_data();
        // For Excel, create a simple table with the JSON as text
        const json_table = [
          {
            "JSON Data": JSON.stringify(json_data, null, 2),
          },
        ];
        const worksheet = XLSX.utils.json_to_sheet(json_table);
        XLSX.utils.book_append_sheet(workbook, worksheet, "Raw JSON Data");
      }

      const filename = `CIPC_Company_Report_${current_date}.xlsx`;
      XLSX.writeFile(workbook, filename);
    },

    async export_json() {
      const current_date = new Date().toISOString().split("T")[0];
      const export_data = {};

      if (this.export_options.company_information) {
        export_data.company_information =
          this.prepare_company_information_data()[0];
      }

      if (this.export_options.director_information) {
        export_data.director_information =
          this.prepare_director_information_data();
      }

      if (this.export_options.address_information) {
        export_data.address_information =
          this.prepare_address_information_data();
      }

      if (this.export_options.raw_json) {
        export_data.raw_data = this.prepare_raw_json_data();
      }

      const json = JSON.stringify(export_data, null, 2);
      const filename = `CIPC_Company_Report_${current_date}.json`;
      this.download_file(json, filename, "application/json");
    },

    download_file(content, filename, content_type) {
      const blob = new Blob([content], { type: content_type });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },

    // Utility: Normalize to array
    normalize_to_array(data) {
      if (Array.isArray(data)) return data;
      if (data && typeof data === "object") return [data];
      return [];
    },
  },
};